/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { FormControl, InputLabel, Select, type SelectChangeEvent } from '@mui/material';
import { useCallback, useMemo } from 'react';
import InputKeyValue from '../display';
import { useFieldContext } from './formContext';

interface Props {
  optionLabelMap: Record<string, string>;
  label?: React.ReactNode;
  disabled?: boolean;
  displayOnly?: boolean;
  size?: 'small' | 'xs' | 'tiny';
}

export function FormSelect({
  optionLabelMap,
  label,
  disabled,
  displayOnly,
  size,
}: Readonly<Props>) {
  const field = useFieldContext<string>();
  const { name, handleChange } = field;
  const value = field.state.value;
  const error = Boolean(field.state.meta.errors[0]?.message);

  const height = useMemo(() => {
    switch (size) {
      case 'tiny':
        return 25;
      case 'xs':
        return 30;
      case 'small':
        return 40;
      default:
        return 40;
    }
  }, [size]);

  const onChange = useCallback(
    (e: SelectChangeEvent) => handleChange(e.target.value),
    [handleChange],
  );

  return useMemo(() => {
    if (displayOnly) {
      return <InputKeyValue value={value} label={label} sx={{ pt: 0, pb: 0 }} />;
    }
    return (
      <FormControl fullWidth error={error} disabled={disabled}>
        <InputLabel id={`${name}-label`}>{label ?? name}</InputLabel>
        <Select
          labelId={`${name}-label`}
          name={name}
          value={value}
          label={label ?? name}
          onChange={onChange}
          size="small"
          disabled={disabled}
          sx={{ height }}
        >
          {Object.entries(optionLabelMap).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </Select>
      </FormControl>
    );
  }, [optionLabelMap, disabled, error, label, name, onChange, value, height, displayOnly]);
}
